import { PriceSortQueryValue, DeviceType, Currency, Page } from '@packages/common';
import { DyPurchaseEvent } from '@lib/dy';
import { DynamicYieldSortData, DynamicYieldEventType, CustomDynamicYieldEventType } from './dynamicYieldDataTypes';
import { getDynamicYieldPageContext } from './dynamicYieldPageContextUtils';

function mapPriceSortValueToDYData(priceSortValue: PriceSortQueryValue): DynamicYieldSortData {
  switch (priceSortValue) {
    case PriceSortQueryValue.ASC: {
      return { sortBy: 'PRICE', sortOrder: 'ASC' };
    }

    case PriceSortQueryValue.DESC: {
      return { sortBy: 'PRICE', sortOrder: 'DESC' };
    }

    default:
    case PriceSortQueryValue.NONE: {
      return { sortBy: 'POPULARITY', sortOrder: 'DESC' };
    }
  }
}

function getClickoutRevenueByDeviceType(deviceType: DeviceType): number {
  switch (deviceType) {
    case DeviceType.mobile: {
      return 0.25;
    }

    default:
    case DeviceType.desktop: {
      return 0.35;
    }
  }
}

function fireDYEvent(globalDyObject: any | undefined, name: string, type: DynamicYieldEventType, eventData: any): void {
  globalDyObject?.API?.('event', {
    name,
    properties: {
      dyType: type,
      ...eventData,
    },
  });
}

function fireCustomDYEvent(globalDyObject: any | undefined, name: string, eventData: any): void {
  globalDyObject?.API?.('event', {
    name,
    properties: {
      ...eventData,
    },
  });
}

// this identify-api event has a different data structure than all the other
// events. So it gets its own fire function
export function fireDYIdentifyAPIEvent(globalDyObject: any | undefined, userIdHash: string): void {
  globalDyObject?.API?.('identify', {
    uid: userIdHash,
    type: 'he',
  });
}

export function fireDYSPAPageView(
  globalDyObject: any | undefined,
  url: string,
  page: Page,
  categoryName: string,
): void {
  globalDyObject?.API?.('spa', {
    context: getDynamicYieldPageContext(url, page, categoryName),
    countAsPageView: true,
  });
}

export function fireAddToWishlistEvent(dyData: any, wishlistedItemId: string): void {
  fireDYEvent(dyData, 'Add to wishlist', DynamicYieldEventType.ADD_TO_WISHLIST, { productId: wishlistedItemId });
}

export function fireRemoveFromWishlistEvent(dyData: any, removedItemId: string): void {
  fireCustomDYEvent(dyData, 'Remove from wishlist', { productId: removedItemId });
}

export function fireFilterItemsByNumericFacetEvent(dyData: any, facetName: string, value: number): void {
  fireDYEvent(dyData, 'Filter by numeric facet', DynamicYieldEventType.FILTER_ITEMS, {
    filterType: facetName,
    filterNumericValue: value,
  });
}

export function fireFilterItemsByNumericRangeFacetEvent(
  dyData: any,
  facetName: string,
  from: number,
  to: number,
): void {
  fireDYEvent(dyData, 'Filter range by price range', DynamicYieldEventType.FILTER_ITEMS, {
    filterType: facetName,
    filterStringValue: `${from}-${to}`,
  });
}

export function fireFilterItemsByStringFacetEvent(dyData: any, facetName: string, value: string): void {
  fireDYEvent(dyData, 'Filter by string facet', DynamicYieldEventType.FILTER_ITEMS, {
    filterType: facetName,
    filterStringValue: value,
  });
}

export function fireRemoveNumericFilterEvent(dyData: any, facetName: string): void {
  fireCustomDYEvent(dyData, CustomDynamicYieldEventType.REMOVE_NUM_FACET, {
    filterType: facetName,
  });
}

export function fireRemoveStringFilterEvent(dyData: any, facetName: string, value: string): void {
  fireCustomDYEvent(dyData, CustomDynamicYieldEventType.REMOVE_STRING_FACET, {
    filterType: facetName,
    filterStringValue: value,
  });
}

export function fireClearAllFiltersEvent(dyData: any): void {
  fireDYEvent(dyData, 'Clear all filters', DynamicYieldEventType.CLEAR_FILTERS, {});
}

export function fireSearchEvent(dyData: any, searchQuery: string): void {
  fireDYEvent(dyData, 'Keyword search', DynamicYieldEventType.KEYWORD_SEARCH, {
    keywords: searchQuery,
  });
}

export function fireSortItemsEvent(dyData: any, priceSortValue: PriceSortQueryValue): void {
  const sortData = mapPriceSortValueToDYData(priceSortValue);
  fireDYEvent(dyData, 'Sort items', DynamicYieldEventType.SORT_ITEMS, sortData);
}

export function fireClickoutEvent(dyData: any, deviceType: DeviceType, productId: string): void {
  const clickoutRevenue = getClickoutRevenueByDeviceType(deviceType);
  fireCustomDYEvent(dyData, CustomDynamicYieldEventType.CLICKOUT, {
    value: clickoutRevenue,
    currency: Currency.euro,
    productId,
    quantity: 1,
  });
}

export function fireLoginEvent(dyData: any, userIdHash: string): void {
  fireDYEvent(dyData, 'Login', DynamicYieldEventType.LOGIN, {
    cuid: userIdHash,
    cuidType: 'customerId',
  });
}

export function fireNewsletterSubscriptionEvent(dyData: any, lowercasedHashedEmail: string): void {
  fireDYEvent(dyData, 'Newsletter subscription', DynamicYieldEventType.NEWSLETTER_SUBSCRIPTION, {
    hashedEmail: lowercasedHashedEmail,
  });
}

//#region For Custom API
export function createPurchaseEvent(deviceType: DeviceType, productId: string): DyPurchaseEvent {
  const clickoutRevenue = getClickoutRevenueByDeviceType(deviceType);
  return {
    name: CustomDynamicYieldEventType.CLICKOUT,
    properties: {
      dyType: 'purchase-v1',
      value: clickoutRevenue,
      currency: Currency.euro,
      cart: [
        {
          productId,
          quantity: 1,
          itemPrice: clickoutRevenue,
        },
      ],
    },
  };
}
//#endregion
